import React, { useState, useEffect, useMemo } from "react";
import Axios from "axios";
import moment from "moment-timezone";
import { DATABASE_URL, SECURITY_FLAG_LIMIT, TYPE_OF_TEST } from "../../appConstants";
import { useParams } from "react-router-dom";
import AuthHeader from "../../services/AuthHeader";
import { useCEFRconverter } from "../../hooks/useCEFRconverter";
import { Alert, Button } from "@mui/material";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLoadingContext } from "../../hooks/useLoadingContext";
import { Loader } from "../../components/common";

import "./Certificate.css";

function Certificate() {
  console.log('Certificate component rendered');
  const params = useParams();
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [testCenterDetails, setTestCenterDetails] = useState(null);
  const [gradeDetails, setGradeDetails] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const { isLoading, startLoading, stopLoading } = useLoadingContext();
  const EXPORT_PAGE_WIDTH = 1123;
  const EXPORT_PAGE_HEIGHT = 794;

  useEffect(() => {
    console.log('Fetching candidate data, params.id:', params.id);
    startLoading();

    Axios.get(DATABASE_URL + "/candidate/" + params.id, {
      headers: AuthHeader(),
    }).then((response) => {
      const candidate = response.data.candidate[0];
      setCandidateDetails(candidate);
      stopLoading();
    }).catch((error) => {
      console.log('Error fetching candidate data:', error);
      stopLoading();
    });
  }, [params.id]);

  useEffect(() => {
    if (candidateDetails) {
      console.log('Fetching test center and grading data');
      startLoading();

      Axios.get(
        DATABASE_URL + "/test_center/" + candidateDetails.test_center_id,
        {
          headers: AuthHeader(),
        }
      ).then((response) => {
        const testCenter = response.data.testCenter[0];
        setTestCenterDetails(testCenter);
        stopLoading();
      }).catch((error) => {
        console.log('Error fetching test center data:', error);
        stopLoading();
      });

      Axios.get(DATABASE_URL + "/grading/candidate/" + candidateDetails.id, {
        headers: AuthHeader(),
      }).then((response) => {
        const grading = response.data;
        setGradeDetails(grading);
        stopLoading();
      }).catch((error) => {
        console.log('Error fetching grading data:', error);
        stopLoading();
      });
    }
  }, [candidateDetails]);

  const formatDate = (date, timezone) => {
    if (!date || !timezone) {
      return '';
    }

    const parsedDate = moment.utc(date).tz(timezone);

    if (!parsedDate.isValid()) {
      console.error('Invalid date');
      return '';
    }

    const dayOfMonth = parsedDate.date();
    const monthName = parsedDate.format('MMM');
    const yearString = parsedDate.year();
    let ordinal = 'th';

    if (dayOfMonth > 3 && dayOfMonth < 21) {
      ordinal = 'th';
    } else {
      if (dayOfMonth % 10 === 1) ordinal = 'st';
      if (dayOfMonth % 10 === 2) ordinal = 'nd';
      if (dayOfMonth % 10 === 3) ordinal = 'rd';
    }

    return (
      <>
        {monthName} {dayOfMonth}
        <sup>{ordinal}</sup> {yearString}
      </>
    );
  };

  const formatDateOfBirth = (date) => {
    if (!date) {
      return '';
    }

    const parsedDate = moment.utc(date);

    if (!parsedDate.isValid()) {
      console.error('Invalid date of birth');
      return '';
    }

    const dayOfMonth = parsedDate.date();
    const monthName = parsedDate.format('MMM');
    const yearString = parsedDate.year();
    let ordinal = 'th';

    if (dayOfMonth > 3 && dayOfMonth < 21) {
      ordinal = 'th';
    } else {
      if (dayOfMonth % 10 === 1) ordinal = 'st';
      if (dayOfMonth % 10 === 2) ordinal = 'nd';
      if (dayOfMonth % 10 === 3) ordinal = 'rd';
    }

    return `${monthName} ${dayOfMonth}${ordinal} ${yearString}`;
  };

  const formattedDateOfBirth = useMemo(() => {
    if (candidateDetails && candidateDetails.date_of_birth) {
      return formatDateOfBirth(candidateDetails.date_of_birth);
    }
    return '';
  }, [candidateDetails]);

  const hasValidGrade = (value) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === "string" && value.trim() === "") {
      return false;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0;
  };

  const certificateReadiness = useMemo(() => {
    if (!candidateDetails || !gradeDetails) {
      return {
        ready: false,
        message: "Certificate availability is being checked.",
      };
    }

    if (candidateDetails.status === 0 || Number(candidateDetails.flag || 0) >= SECURITY_FLAG_LIMIT) {
      return {
        ready: false,
        message: "Certificate is unavailable because this test was suspended due to security warnings.",
      };
    }

    const hasOverallGrade = hasValidGrade(candidateDetails.grade);
    const hasListeningAndReadingGrade = hasValidGrade(gradeDetails.listeningAndReadingGrade);
    const hasSpeakingGrade = hasValidGrade(gradeDetails.speakingGrade);
    const hasWritingGrade = hasValidGrade(gradeDetails.writingGrade);

    let ready = hasOverallGrade;

    if (candidateDetails.type_of_test_id === 1) {
      ready = ready && hasListeningAndReadingGrade && hasSpeakingGrade && hasWritingGrade;
    } else if (candidateDetails.type_of_test_id === 2) {
      ready = ready && hasSpeakingGrade && hasWritingGrade;
    } else if (candidateDetails.type_of_test_id === 3) {
      ready = ready && hasListeningAndReadingGrade;
    }

    return {
      ready,
      message: ready
        ? ""
        : "Certificate download will be available when grading is finished.",
    };
  }, [candidateDetails, gradeDetails]);

  const waitForImages = async (container) => {
    const images = Array.from(container.querySelectorAll("img"));

    await Promise.all(images.map((image) => {
      if (image.complete && image.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const done = () => resolve();
        image.addEventListener("load", done, { once: true });
        image.addEventListener("error", done, { once: true });
      });
    }));
  };

  const downloadCertificate = async () => {
    if (!candidateDetails || !certificateReadiness.ready) {
      console.log('Cannot download certificate: grading is incomplete or candidateDetails is null');
      return;
    }

    startLoading();
    setDownloadError("");

    try {
      const certificateContainer = document.getElementById("certificate-container");

      if (!certificateContainer) {
        throw new Error("Certificate container not found.");
      }

      const exportRoot = document.createElement("div");
      const exportWrapper = certificateContainer.cloneNode(true);
      const exportPages = Array.from(exportWrapper.querySelectorAll(".page"));

      exportRoot.className = "certificate-export-root";
      exportRoot.appendChild(exportWrapper);
      document.body.appendChild(exportRoot);

      exportWrapper.style.width = `${EXPORT_PAGE_WIDTH}px`;
      exportWrapper.style.maxWidth = "none";
      exportWrapper.style.gap = "0";

      exportPages.forEach((page) => {
        page.style.width = `${EXPORT_PAGE_WIDTH}px`;
        page.style.height = `${EXPORT_PAGE_HEIGHT}px`;
        page.style.maxWidth = "none";
        page.style.aspectRatio = "auto";
      });

      try {
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }

        await waitForImages(exportWrapper);

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [EXPORT_PAGE_WIDTH, EXPORT_PAGE_HEIGHT],
          compress: true,
        });

        for (const [index, page] of exportPages.entries()) {
          const canvas = await html2canvas(page, {
            backgroundColor: "#ffffff",
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: EXPORT_PAGE_WIDTH,
            height: EXPORT_PAGE_HEIGHT,
            windowWidth: EXPORT_PAGE_WIDTH,
            windowHeight: EXPORT_PAGE_HEIGHT,
            scrollX: 0,
            scrollY: 0,
            logging: false,
          });

          if (index > 0) {
            pdf.addPage([EXPORT_PAGE_WIDTH, EXPORT_PAGE_HEIGHT], "landscape");
          }

          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.98),
            "JPEG",
            0,
            0,
            EXPORT_PAGE_WIDTH,
            EXPORT_PAGE_HEIGHT,
            undefined,
            "FAST"
          );
        }

        pdf.save(`${candidateDetails.first_name}-${candidateDetails.last_name}-CERTIFICATE-OF-ACHIEVEMENT.pdf`);
      } finally {
        document.body.removeChild(exportRoot);
      }
    } catch (error) {
      const errorMessage = error && error.message ? error.message : "Unable to download certificate.";
      setDownloadError(errorMessage);
      console.error('Error downloading certificate:', error);
    } finally {
      stopLoading();
    }
  };

  if (isLoading || !candidateDetails) {
    return (
      <div className="loader-display">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="download-btn-container">
        <Button
          variant="contained"
          color="primary"
          type="submit"
          size="large"
          onClick={downloadCertificate}
          disabled={isLoading || !certificateReadiness.ready}
        >
          Download Certificate
        </Button>
      </div>
      {!certificateReadiness.ready && (
        <Alert severity="info">{certificateReadiness.message}</Alert>
      )}
      {downloadError && (
        <Alert severity="error">{downloadError}</Alert>
      )}
      {certificateReadiness.ready && (
        <div className="certificate-wrapper certificate-wrapper--preview" id="certificate-container">
          <div className="page first-page">
            <div className="title">
              <h3>CERTIFICATE</h3>
              <p>OF ACHIEVEMENT</p>
            </div>
            <div className="content">
              <p>This is to certify that</p>
              <h3>
                {candidateDetails.last_name}, {candidateDetails.first_name}
              </h3>
              <p>
                Has achieved an overall score of{" "}
                <strong>{candidateDetails.grade ? candidateDetails.grade : 0}</strong> on the ELL Assessment
                Test which corresponds to a{" "}
                <strong>({useCEFRconverter(Number(candidateDetails.grade))})</strong> in
                the Common European Framework of Reference.
              </p>
              <p>{formatDate(moment(candidateDetails.test_date).format(), candidateDetails.time_zone)}</p>
            </div>
            <div className="logo">
              <img src="/images/square_logo.png" alt="logo" />
            </div>
            <div className="verification-code">{candidateDetails.id}</div>
            <div className="sign first-sign">
              <img src="/images/rebecca_sign.png" alt="Rebecca Verbeem's signature" />
              <p>
                Rebecca Verbeem
                <br />
                Operations Manager, Everybody Loves Languages
              </p>
            </div>
            <div className="sign second-sign">
              <img src="/images/gail_sign.png" alt="Gali Bar-Ziv's signature" />
              <p>
                Gali Bar-Ziv
                <br />
                CEO, Everybody Loves Languages
              </p>
            </div>
          </div>
          <div className="page second-page">
            <div className="title-image">
              <h3>
                OFFICIAL
                <br />
                SCORE
                <br />
                REPORT
              </h3>
              <img src={candidateDetails.image_string} alt="Candidate's image" />
              <p>{candidateDetails.id}</p>
            </div>
            <div className="result">
              <img src="/images/ell-logo.png" alt="ELL logo" />
              <div className="candidate-info">
                <p>Last Name: {candidateDetails.last_name}</p>
                <p>First Name: {candidateDetails.first_name}</p>
                <p>Date of Birth: {formattedDateOfBirth}</p>
                <p>Government ID: {candidateDetails.government_id}</p>
                <p>Test Date: {formatDate(moment(candidateDetails.test_date).format(), candidateDetails.time_zone)}</p>
                <p>Test Type: {TYPE_OF_TEST[candidateDetails.type_of_test_id]}</p>
              </div>
              <div className="score">
              <div className="left">
              <p>
                <strong>OVERALL SCORE:</strong> {candidateDetails.grade} ({useCEFRconverter(Number(candidateDetails.grade))})
              </p>
              <p>
                <strong>
                  1<sup>st</sup> SECTION - LISTENING AND READING:
                </strong>
                {gradeDetails && `${gradeDetails.listeningAndReadingGrade} (${useCEFRconverter(gradeDetails.listeningAndReadingGrade)})`}
              </p>
              <p>
                <strong>
                  2<sup>nd</sup> SECTION - SPEAKING:
                </strong>
                {gradeDetails && `${gradeDetails.speakingGrade} (${useCEFRconverter(gradeDetails.speakingGrade)})`}
              </p>
              <p>
                <strong>
                  2<sup>nd</sup> SECTION - WRITING:
                </strong>
                {gradeDetails && `${gradeDetails.writingGrade} (${useCEFRconverter(gradeDetails.writingGrade)})`}
              </p>
            </div>
                <div className="right">
                  <p>Test Center: </p>
                  <p>{testCenterDetails && testCenterDetails.name}</p>
                  <p>Test Center ID: </p>
                  <p>{testCenterDetails && testCenterDetails.id}</p>
                  <p>Location:</p>
                  <p>{testCenterDetails && testCenterDetails.address}</p>
                  <p>Issued on: </p>
                  <p>{formatDate(new Date(), candidateDetails.time_zone)}</p>
                </div>
              </div>
            </div>
            <div className="sign first-sign">
              <img src="/images/rebecca_sign.png" alt="Rebecca Verbeem's signature" />
              <p>
                Rebecca Verbeem
                <br />
                Operations Manager, Everybody Loves Languages
              </p>
            </div>
            <div className="sign second-sign">
              <img src="/images/gail_sign.png" alt="Gali Bar-Ziv's signature" />
              <p>
                Gali Bar-Ziv
                <br />
                CEO, Everybody Loves Languages
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Certificate;
